<script setup lang="ts">
import type {PropType} from 'vue'
import {computed, defineEmits, defineProps, onMounted, onUnmounted, ref} from 'vue'
import {useI18n} from 'vue-i18n'
import {filterListType, filterType} from "@/assets/ts/types/filters";
import {FilterLimiter} from "@/assets/ts/FilterLimiter";
import SidebarHeadlineBox from "@/components/sidebar/sidebarHeadlineBox.vue";
import SidebarTextList from "@/components/sidebar/sidebarTextList.vue";
import SidebarSwitch from "@/components/sidebar/sidebarSwitch.vue";
import SidebarIcons from "@/components/sidebar/sidebarIcons.vue";
import SidebarPriceRange from "@/components/sidebar/sidebarPriceRange.vue";

const props = defineProps({
  mobileFiltersOpen: {
    type: Boolean,
    default: false
  },
  filters: {
    type: Object as PropType<filterListType>,
    default: () => ({} as filterListType)
  },
  activeFilters: {
    type: Object as PropType<Record<string, string>>,
    default: () => ({})
  },
  catalogItemType: {
    type: String,
    default: ''
  }
})

const emit = defineEmits<{
  (e: 'update:mobileFiltersOpen', value: boolean): void
  (e: 'update-filter', filterName: string, value: string): void
  (e: 'remove-filter', filterName: string): void
  (e: 'reset-filters'): void
}>()

const {t} = useI18n()

const filterLimiter = new FilterLimiter()

const resetFilters = () => {
  emit('reset-filters')
}

/*
 * filter images
 */

const imageFilters: Record<string, string> = {
  "catalogItemType": "https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/catalog/{value}.webp",
  "color": "https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/colors/{value}.jpg",
  "metal": "https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/metals/{color}-{value}.webp",
  "stone_form": "https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/stones/forms/{value}@2x.png",
  "stone_size": "https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/stones/forms/{stone_form}@2x.png",
  "stone_setting": "https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/catalog/stones/settings/{value}.webp",
  "stone_quality": "https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/stones/qualities/{stone_form}-{value}@2x.webp",
  "side_stones": "https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/stones/side_stones/side_stones_{value}_{color}.webp",
  "stone_color": "https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/stones/qualities/{stone_form}-{value}@2x.webp",
  "width": "https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/width/{value}.jpg",
  "finish": "https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/finish/{value}.jpg",
  "stone_count": "https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/stones/stone_count/{value}.jpg",
  "stones": "https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/stones/side_stones/side_stones_{value}_yellow.webp",
  "profile": "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/cross_sections/{value}/{value}_30_15_yellow.webp"
}

const processedFilters = computed(() => {
  const color = (props.activeFilters['color'] || "yellow").toLowerCase()
  const stone_form = (props.activeFilters['stone_form'] || "round").toLowerCase()

  const limitedFilters = filterLimiter.limitFilters(props.filters, props.activeFilters);

  const filtersWithImages = Object.entries(limitedFilters).reduce((acc, [key, filter]) => {
    let image = imageFilters[key] || filter.image

    if (image) {
      image = image.replace('{color}', color).replace('{stone_form}', stone_form)
    }

    acc[key] = {
      ...filter,
      image: image
    }

    return acc
  }, {} as filterListType)

  const sortedEntries = Object.entries(filtersWithImages)
      .filter(([key]) => key !== 'price_sort')
      .sort(([, a], [, b]) => a.order - b.order)

  const catalogItemTypeEntry = sortedEntries.find(([key]) => key === 'catalogItemType')

  if (catalogItemTypeEntry) {
    const otherEntries = sortedEntries.filter(([key]) => key !== 'catalogItemType')
    const fastDeliveryIndex = otherEntries.findIndex(([key]) => key === 'fastDelivery')

    if (fastDeliveryIndex !== -1) {
      otherEntries.splice(fastDeliveryIndex, 0, catalogItemTypeEntry)
      return Object.fromEntries(otherEntries)
    }
  }

  return Object.fromEntries(sortedEntries)
})

/*
 * filter number formatting
 */

const numberFilters: Record<string, { decimal: number, unit: string }> = {
  "stone_size": {"decimal": 3, "unit": "ct."},
  "price": {"decimal": 0, "unit": "€"},
  "width": {"decimal": 1, "unit": "mm"}
}

const formatNumber = (filterName: string, value: number): string => {
  const config = numberFilters[filterName]
  if (!config) return ""

  const roundedValue = Number(parseFloat(value.toString()).toFixed(config.decimal))
  const localNumber = roundedValue.toLocaleString('de-DE')

  return config.unit ? `${localNumber} ${config.unit}` : localNumber
}

const getLabel = (filterName: string, value: string | number): string => {
  if (value === '') {
    if (filterName === 'price_sort') {
      return t('components.catalog.filter.price_sort.default.label')
    }
    return t('components.catalog.all')
  }

  if (filterName === 'catalogItemType') {
    return t(`components.catalog.catalogs.${value}`)
  }

  if (filterName === 'price') {
    const parts = value.toString().split('-')
    if (parts.length === 2) {
      const maxPrice = (props.filters.price as any)?.max ?? 10000
      const minDisplay = Number(parts[0]) === maxPrice ? '∞' : Number(parts[0]).toLocaleString('de-DE')
      const maxDisplay = Number(parts[1]) === maxPrice ? '∞' : Number(parts[1]).toLocaleString('de-DE')
      return `${minDisplay} € - ${maxDisplay} €`
    }
  }

  if (numberFilters[filterName] && parseFloat(value.toString())) {
    return formatNumber(filterName, value as number)
  }

  if (filterName === 'diamond') {
    return value.toString().toUpperCase()
  }

  if (filterName === 'price_sort') {
    return t(`components.catalog.filter.price_sort.${value}.label`)
  }

  return t(`components.catalog.filter.${filterName}.${value.toString()}`)
}

const getFilterOptions = (filterName: string, rawOptions: filterType): any => {
  const options = rawOptions.values.reduce((acc, value) => {
    acc[value] = getLabel(filterName, value)
    return acc
  }, {} as Record<string, string>)

  if (filterName !== 'catalogItemType' && filterName !== 'stone_type') {
    options[""] = t('components.catalog.all')
  }

  return {
    ...rawOptions,
    values: options
  }
}

const getStoneTypeLabel = computed(() => {
  // return earth or lab from the active filters if stone_type is part of the active filter
  if (props.activeFilters.stone_type) {
    return props.activeFilters.stone_type
  }
  return 'earth'
})

const isMobile = ref(false);
const checkMobile = () => {
  isMobile.value = window.innerWidth < 1024;
  if (!isMobile.value && props.mobileFiltersOpen) {
    emit('update:mobileFiltersOpen', false);
  }
};

const toggleMobileFilters = () => {
  const newValue = !props.mobileFiltersOpen;
  emit('update:mobileFiltersOpen', newValue);
  if (newValue) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
};

const indicatorRefs = ref<Record<string, any>>({});
const scrollToFilter = (key: string) => {
  const el = indicatorRefs.value[key];
  if (el) {
    el.scrollIntoView({behavior: 'smooth', block: 'start'});
  }
};

const isFilterActive = (key: string) => {
  return props.activeFilters[key] !== undefined && props.activeFilters[key] !== '';
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
  document.body.style.overflow = '';
});

</script>

<template>
  <div class="w-full relative">
    <!-- Desktop View -->
    <template v-if="!isMobile">
      <div
          v-for="(options, filterName) in processedFilters"
          :key="filterName as string"
          class="pr-8"
      >
        <sidebar-headline-box
            :filter-label="$t(`components.catalog.filter.${filterName.toString()}.label`)"
            :filter-value="getLabel(filterName.toString(), activeFilters[filterName] || '')"
            :init-filter-open="isFilterActive(filterName.toString()) || options.prio"
            :raw-filter-value="activeFilters[filterName]"
            :raw-stone-type="getStoneTypeLabel"
            :show-info="catalogItemType.indexOf('wedding') === -1 || ( catalogItemType.indexOf('wedding') !== -1 && filterName !== 'stone_quality')"
            :filter-name="filterName.toString()"
        >
          <template v-if="filterName === 'stone_type' || filterName === 'fastDelivery'">
            <sidebar-switch
                :filter="getFilterOptions(filterName.toString(), options)"
                :active-value="activeFilters[filterName]"
                mode="small"
                @update="(val) => emit('update-filter', filterName as string, val)"
            ></sidebar-switch>
          </template>
          <template v-else-if="filterName === 'price'">
            <sidebar-price-range
                :filter="options"
                :active-value="activeFilters[filterName] || ''"
                :active-price-sort="activeFilters['price_sort'] || ''"
                :catalog-item-type="catalogItemType"
                class="px-6"
                @update="(val) => emit('update-filter', filterName as string, val)"
                @update:price-sort="(val) => val ? emit('update-filter', 'price_sort', val) : emit('remove-filter', 'price_sort')"
            />
          </template>
          <template v-else>
            <sidebar-icons
                v-if="options.image && options.image != ''"
                :filter="getFilterOptions(filterName.toString(), options)"
                :active-value="activeFilters[filterName]"
                :single-select="true"
                :mode="filterName === 'catalogItemType' ? 'normal' : 'small'"
                @update="(val) => emit('update-filter', filterName as string, val)"

            ></sidebar-icons>
            <sidebar-text-list
                v-else
                :filter="getFilterOptions(filterName.toString(), options)"
                :active-value="activeFilters[filterName]"
                :single-select="true"
                @update="(val) => emit('update-filter', filterName as string, val)"
            />
          </template>
        </sidebar-headline-box>
      </div>

      <div class="mt-8 pr-8 flex justify-center mb-10">
        <button
            @click="resetFilters"
            class="text-xs uppercase tracking-widest text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          {{ $t('components.catalog.filterSideBar.cta.reset') }}
        </button>
      </div>
    </template>

    <!-- Mobile View -->
    <div v-else class="w-full">
      <!-- Mobile Filter Button -->
      <div class="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
        <button
            @click="toggleMobileFilters"
            class="bg-[#a99d8b] text-white px-8 py-3 rounded-full shadow-2xl flex items-center gap-2 uppercase tracking-[0.2em] text-sm font-medium hover:bg-[#968977] transition-all active:scale-95 text-nowrap"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd"
                  d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                  clip-rule="evenodd"/>
          </svg>
          {{ t('components.catalog.more_filters.parameters', {count: Object.keys(activeFilters).length}) }}
        </button>
      </div>

      <!-- Mobile Filter Overlay -->
      <transition name="fade">
        <div v-if="mobileFiltersOpen" class="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 lg:hidden"
             @click="toggleMobileFilters"></div>
      </transition>
      <transition name="slide-up">
        <div v-if="mobileFiltersOpen"
             class="fixed bottom-0 left-0 right-0 bg-white z-50 rounded-t-[2.5rem] shadow-2xl flex flex-col max-h-[85vh] lg:hidden">
          <!-- Overlay Header -->
          <div class="p-4 flex flex-col items-center border-b border-gray-50">
            <div class="w-12 h-1.5 bg-gray-200 rounded-full mb-6 mt-2"></div>

            <!-- Filter Indicators -->
            <div class="w-full overflow-x-auto no-scrollbar pb-2">
              <div class="flex gap-4 px-2">
                <button
                    v-for="(options, filterName) in processedFilters"
                    :key="'indicator-' + filterName"
                    @click="scrollToFilter(filterName.toString())"
                    class="whitespace-nowrap px-4 py-1.5 rounded-full text-[11px] uppercase tracking-wider transition-all border"
                    :class="isFilterActive(filterName.toString()) ? 'bg-[#a99d8b] text-white border-[#a99d8b]' : 'bg-gray-50 text-gray-500 border-gray-100'"
                >
                  {{ $t(`components.catalog.filter.${filterName}.label`) }}
                </button>
              </div>
            </div>
          </div>

          <!-- Overlay Content -->
          <div class="overflow-y-auto grow p-4">
            <div
                v-for="(options, filterName) in processedFilters"
                :key="'mobile-' + filterName"
                :ref="el => { if (el) indicatorRefs[filterName] = el }"
                class="scroll-mt-4"
            >
              <sidebar-headline-box
                  :filter-label="$t(`components.catalog.filter.${filterName}.label`)"
                  :filter-value="getLabel(filterName.toString(), activeFilters[filterName] || '')"
                  :raw-filter-value="activeFilters[filterName]"
                  :init-filter-open="isFilterActive(filterName.toString())"
                  :filter-name="filterName.toString()"
              >
                <template v-if="filterName === 'stone_type' || filterName === 'fastDelivery'">
                  <sidebar-switch
                      :filter="getFilterOptions(filterName.toString(), options)"
                      :active-value="activeFilters[filterName]"
                      mode="small"
                      @update="(val) => emit('update-filter', filterName as string, val)"
                  ></sidebar-switch>
                </template>
                <template v-else-if="filterName === 'price'">
                  <sidebar-price-range
                      :filter="options"
                      :active-value="activeFilters[filterName] || ''"
                      :active-price-sort="activeFilters['price_sort'] || ''"
                      :catalog-item-type="catalogItemType"
                      @update="(val) => emit('update-filter', filterName as string, val)"
                      @update:price-sort="(val) => val ? emit('update-filter', 'price_sort', val) : emit('remove-filter', 'price_sort')"
                  />
                </template>
                <template v-else>
                  <sidebar-icons
                      v-if="options.image && options.image != ''"
                      :filter="getFilterOptions(filterName.toString(), options)"
                      :active-value="activeFilters[filterName]"
                      :single-select="true"
                      mode="small"
                      @update="(val) => emit('update-filter', filterName as string, val)"

                  ></sidebar-icons>
                  <sidebar-text-list
                      v-else
                      :filter="getFilterOptions(filterName.toString(), options)"
                      :active-value="activeFilters[filterName]"
                      :single-select="true"
                      @update="(val) => emit('update-filter', filterName as string, val)"
                  />
                </template>
              </sidebar-headline-box>
            </div>
          </div>

          <!-- Overlay Footer -->
          <div class="p-6 border-t border-gray-100 flex flex-col items-center gap-4 bg-gray-50/50">
            <button
                @click="toggleMobileFilters"
                class="w-full max-w-xs bg-[#a99d8b] text-white py-4 rounded-xl uppercase tracking-[0.2em] text-sm font-bold shadow-lg shadow-[#a99d8b]/20 active:scale-[0.98] transition-all"
            >
              {{ $t('components.catalog.filterSideBar.cta.showResults') }}
            </button>
            <button
                @click="resetFilters"
                class="text-xs uppercase tracking-widest text-gray-500 hover:text-gray-900 transition-colors"
            >
              {{ $t('components.catalog.filterSideBar.cta.reset') }}
            </button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>